import { Box, Button, Link, Paper, Typography } from '@mui/material';
import TextField from '../../components/TextField/TextField';
import {
  SignInSx,
  PaperSx,
  TitleSx,
  ExplanationTextSx,
  PasswordSx,
  LinkSx,
  LoginButtonSx,
  EyeContainerSx,
  formSchema,
  FormData,
  ErrorMessageSx,
} from './SignInProps';
import EyeIcon from '../../assets/icons/EyeIcon';
import EyeClosedIcon from '../../assets/icons/EyeClosedIcon';
import { useCustomForm } from '../../hooks/useCustomForm';
import ControlledField from '../../components/ControlledField/ControlledField';
import { useState } from 'react';
import { useAuth } from '../../store/authAtom';
import { useTabNavigation } from '../../store/mainTabsStore';
import { TestIds } from '../../utils/dataTestIds';

function SignIn() {
  const { makeTabActive } = useTabNavigation();
  const { control, handleSubmit } = useCustomForm(
    formSchema,
    {},
    {
      defaultValues: {
        emailOrUsername: '',
        password: '',
      },
    }
  );
  const [passwordIsVisible, setPasswordIsVisible] = useState(false);
  const [signInFailed, setSignInFailed] = useState(false);

  const { login } = useAuth();

  async function handleLogin(formData: FormData) {
    try {
      setSignInFailed(false);
      const { emailOrUsername, password } = formData;
      await login(emailOrUsername, password);
      makeTabActive('/');
    } catch {
      setSignInFailed(true);
    }
  }

  return (
    <Box sx={SignInSx}>
      <Paper
        sx={PaperSx}
        component={'form'}
        onSubmit={handleSubmit(handleLogin)}
      >
        <Typography sx={TitleSx}>Sign in</Typography>
        <Typography sx={ExplanationTextSx}>
          Enter your username/email and password to sign in.
        </Typography>

        <ControlledField
          name="emailOrUsername"
          control={control}
          component={
            <TextField
              label="Username or Email"
              placeholder="Enter your username or email"
              required
              fullWidth
              data-testid={TestIds.SignIn.Email}
            />
          }
        />

        <ControlledField
          name="password"
          control={control}
          component={
            <TextField
              label="Password"
              placeholder="Enter your password"
              fullWidth
              required
              autocompleteHtmlAttribute="new-password"
              sx={PasswordSx}
              type={passwordIsVisible ? 'text' : 'password'}
              endAdornment={
                <Box
                  onClick={() => setPasswordIsVisible((visible) => !visible)}
                  sx={EyeContainerSx}
                >
                  {passwordIsVisible ? (
                    <EyeClosedIcon data-testid={TestIds.SignIn.ClosedEyeIcon} />
                  ) : (
                    <EyeIcon data-testid={TestIds.SignIn.OpenedEyeIcon} />
                  )}
                </Box>
              }
              data-testid={TestIds.SignIn.Password}
            />
          }
        />
        <Link
          href="/forgot-password"
          underline="none"
          sx={LinkSx}
          data-testid={TestIds.SignIn.ForgotPassword}
        >
          Forgot password?
        </Link>
        <Button
          sx={LoginButtonSx}
          type="submit"
          variant="contained"
          data-testid={TestIds.SignIn.Login}
        >
          Login
        </Button>
        {signInFailed && (
          <Typography sx={ErrorMessageSx}>
            Invalid email or password. Please try again.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

export default SignIn;
